import { useRef } from 'react';

/**
 * La fenêtre de 24 h de WhatsApp, et la clé d'idempotence — #84.
 *
 * Ces deux-là n'ont rien à voir entre elles, sinon qu'aucune ne doit être
 * réinventée au site d'appel.
 */

/**
 * La fenêtre est-elle ouverte ?
 *
 * **Une lecture, jamais un calcul.** #19 a mesuré que l'horloge était écrite trois
 * fois dans le dépôt et que **les trois se trompaient** : elles comparaient
 * `updated_at`, repoussé dès qu'on touche la ligne — **2 852 des 3 252 entrants
 * WhatsApp (88 %) portent un `updated_at` postérieur à leur `created_at`**. Le
 * garde-fou s'ouvrait donc tout seul quand quelqu'un ouvrait le fil, et les 7
 * envois qui ont franchi le mur ont **tous** été rejetés par Meta en `131047`.
 *
 * La PR #41 a réglé ça côté serveur : `Customer::whatsappFreeTextUntil()` calcule
 * sur `created_at`, en un seul endroit, et `/customer-basics` expose le résultat.
 * En ajouter une quatrième implémentation ici garantirait qu'un correctif futur en
 * oublie une. On lit.
 *
 * `null` = aucun entrant WhatsApp : la fenêtre n'a jamais été ouverte.
 */
export function fenetreOuverte(jusqua?: string | null, maintenant: Date = new Date()): boolean {
    if (!jusqua) return false;
    const limite = new Date(jusqua);
    if (Number.isNaN(limite.getTime())) return false;
    return limite.getTime() > maintenant.getTime();
}

/**
 * Depuis combien de jours la cliente n'a-t-elle pas écrit ?
 *
 * #19 §6 : **pas de compte à rebours** — 0,6 % des envois tombent dans la tranche
 * 22-24 h, et un compteur serait affiché ~100 % du temps pour ça. Mais l'état
 * fermé dit **depuis quand** : « n'a pas écrit depuis 3 jours » est actionnable,
 * « fenêtre fermée » ne l'est pas.
 *
 * On déduit les 24 h de la fenêtre pour retrouver la date du dernier entrant.
 */
export function joursDepuisDernierEntrant(
    jusqua?: string | null,
    maintenant: Date = new Date(),
): number | null {
    if (!jusqua) return null;
    const limite = new Date(jusqua);
    if (Number.isNaN(limite.getTime())) return null;

    const dernierEntrant = limite.getTime() - 24 * 3_600_000;
    return Math.max(0, Math.floor((maintenant.getTime() - dernierEntrant) / 86_400_000));
}

/** `crypto.randomUUID` n'existe pas partout (contextes non sécurisés, jsdom ancien). */
function genererCle(): string {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Une clé d'idempotence **par message**, pas par tentative.
 *
 * C'est tout l'acquis de #70 et #69 : un échec réseau laisse le brouillon en place
 * et l'utilisateur retape « envoyer » ; sans clé stable, le second tap produit un
 * doublon quand le premier avait en fait abouti. `renouveler()` s'appelle une fois
 * l'envoi **réussi**, pour que le message suivant ait la sienne.
 */
export function useCleIdempotence(): { cle: () => string; renouveler: () => void } {
    const ref = useRef<string | null>(null);

    return {
        cle: () => {
            if (!ref.current) ref.current = genererCle();
            return ref.current;
        },
        renouveler: () => {
            ref.current = null;
        },
    };
}
